import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import type { TemplateParamGroup } from '../types'

interface ColumnOpts {
  t: TFunction
  /** İki dilli alanlar tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  onEdit: (row: TemplateParamGroup) => void
  onToggle: (row: TemplateParamGroup, active: boolean) => void
  /** Durum isteği sürerken satır kontrolleri kilitlenir (çift gönderim engeli). */
  busyId?: number | null
  /**
   * Salt okunur kip (126 — Kısıtlı Yetkili asistan): durum anahtarı görünür ama pasiftir (durum
   * okunabilsin), Düzenle çizilmez. Grubun tek düzenlenebilir bilgisi olan ad tabloda zaten göründüğü
   * için ayrı bir görüntüleme eylemine gerek yoktur.
   */
  readOnly?: boolean
}

/**
 * Parametre Grubu tablosu sütunları (059 US5).
 *
 * EN ad boşsa hücre **boş** bırakılır — TR ad kopyalanmaz, `-`/`—` yazılmaz (anayasa: boş hücre
 * gösterimi). Böylece eksik çeviriler göz taramasıyla fark edilir.
 *
 * **Sıralama sunucu-taraflıdır** (`useTableSort`): üç veri sütunu da `sortable`, aksiyon sütunu
 * değildir. Alan adları sunucudaki beyaz listeyle birebir aynıdır (`nameTr`, `nameEn`,
 * `recordStatus`); durum sütunu etiket gösterir ama sayısal `recordStatus` üzerinden sıralanır.
 *
 * <p><b>Gruplarda yalnız DÜZENLEME vardır</b> (2026-08-03 kararı): satırda **silme yoktur** ve
 * ekranda **yeni grup ekleme yoktur** — gruplar sabit bir sınıflandırmadır, parametrelerin bağlı
 * olduğu bir referans kümesidir; ekleme/silme veri tabanı tarafında yapılır. Kullanımdan kaldırmak
 * için **durum anahtarı** (Aktif/Pasif) kullanılır: pasif grup yeni parametreye atanamaz ama mevcut
 * kayıtların bağı kopmaz — silmenin aksine geri alınabilir.
 */
export function templateParamGroupColumns({
  t,
  localized,
  onEdit,
  onToggle,
  busyId,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  return [
    <Column
      key="name"
      // Sıralama SUNUCUDA (`lazy` tablo): arka uç beyaz listesi artık dile duyarlı, yani İngilizce
      // arayüzde NAME_EN üzerinden sıralar. İstemciye `sortFunction` koymak yalnız açık sayfayı
      // yeniden dizer ve sunucu sıralamasını bozardı.
      field="nameTr"
      header={t('admin.templateParameters.groups.columns.name')}
      sortable
      body={(row: TemplateParamGroup) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="status"
      header={t('admin.templateParameters.groups.columns.status')}
      style={{ width: '8rem' }}
      // Hücre etiket (Badge) basar → `field` yok; sıralama alanı açıkça verilir.
      sortable
      sortField="recordStatus"
      body={(row: TemplateParamGroup) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('admin.templateParameters.status.active')
              : t('admin.templateParameters.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.templateParameters.groups.columns.actions')}
      style={{ width: '5rem' }}
      body={(row: TemplateParamGroup) => (
        <div className="admin-row-actions">
          <InputSwitch
            className="admin-switch"
            checked={row.recordStatus === 1}
            disabled={readOnly || busyId === row.id}
            onChange={(e) => onToggle(row, Boolean(e.value))}
            aria-label={row.nameTr}
            // Salt okunurda anahtar yalnız durumu gösterir; "Aktif/Pasif yap" ipucu yanlış vaat olurdu.
            tooltip={readOnly ? undefined : t('admin.templateParameters.actions.toggle')}
            tooltipOptions={{ position: 'bottom' }}
          />
          {!readOnly && (
            <Button
              icon="pi pi-pencil"
              rounded
              text
              aria-label={t('admin.templateParameters.actions.edit')}
              tooltip={t('admin.templateParameters.actions.edit')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onEdit(row)}
            />
          )}
        </div>
      )}
    />,
  ]
}
